import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import '../styles/FunnelView.css';

// Coverage-funnel lens over the SAME cached snapshot the matrix uses.
// Consumes the payload from GET /api/matrix/:id/funnel (computed by
// funnel_engine.compute_funnel on the backend).
//
// Props:
//   funnel        - { modes, kpis, drill, level_count, chrome? }
//   onViewInMatrix(segmentKey) - optional; jumps back to the grid
//   compact       - stage bars only (dashboard panel cards): no mode toggle,
//                   KPI tiles, legend or drill-down. Always counts per level.
//
// PARAMETERISED, NEVER FORKED. The Training panel's funnel is a different question over a
// different snapshot, but the same picture: stages of a population, green carrying on and red
// leaking, with a ribbon tapering green → green. It reuses this component by passing a
// `chrome` object that switches off the parts that are traceability-specific (the two count
// modes, the KPI tiles, the drill-down) and supplies its own legend; a stage may also carry its
// own `foot` node, `foot_kind` and `unit`. An absent `chrome` is today's behaviour exactly, so
// the trace matrix and the dashboard cards are untouched. A second copy of this file would be
// one refactor away from losing the ribbon geometry and the segment size tiers.

const KIND_CLASS = { good: 'good', bad: 'bad', warn: 'warn' };
const STATUS_LABEL = {
  covered: 'Traced', gap: 'Gap', pass: 'Pass', fail: 'Fail', notrun: 'Not executed',
};
const STATUS_KIND = {
  covered: 'good', gap: 'bad', pass: 'good', fail: 'bad', notrun: 'warn',
};

function FunnelView({ funnel, onViewInMatrix, compact = false }) {
  const { modes = {}, kpis = {}, chrome = {} } = funnel || {};
  // "level" = real items per level · "flow" = requirement flow
  const [mode, setMode] = useState('level');
  // Tallest bar in px. A panel card has far less room than the full view.
  const barMax = compact ? 132 : 290;
  const active = modes[mode] || { stages: [], drill: {} };
  const stages = active.stages || [];
  const drill = active.drill || {};
  const unit = chrome.unit || (mode === 'flow' ? 'reqs' : 'items');

  // Which chrome this funnel wants. Absent `chrome` ⇒ all of it, i.e. the traceability view.
  const showModes = !compact && chrome.modes !== false;
  const showKpis = !compact && chrome.kpis !== false;
  const showLegend = !compact && chrome.legend !== false;
  const showDrill = !compact && chrome.drill !== false;
  // The per-stage hints are prose, not numbers. A caller that carries a lot of them (the
  // Training funnel) can hide them behind its own info toggle; absent ⇒ shown, as before.
  const showFoots = chrome.foots !== false;

  const ribbonsRef = useRef(null);
  const funnelRef = useRef(null);
  const stageRefs = useRef([]);

  // Default drill selection: the deepest "not covered" leak, else first segment
  const defaultSegKey = useMemo(() => {
    for (const s of stages) {
      const bad = s.segments.find(seg => seg.kind === 'bad' && seg.count > 0);
      if (bad) return `${s.id}-${bad.key}`;
    }
    const first = stages[0];
    return first ? `${first.id}-${first.segments[0].key}` : null;
  }, [stages]);

  const [selected, setSelected] = useState(defaultSegKey);
  useEffect(() => { setSelected(defaultSegKey); }, [defaultSegKey]);

  const maxTotal = useMemo(
    () => Math.max(1, ...stages.map(s => s.total || 0)),
    [stages]
  );

  // Green "survival" ribbons connecting each stage's covered/pass segment
  const drawRibbons = useCallback(() => {
    const svg = ribbonsRef.current;
    const funnelEl = funnelRef.current;
    if (!svg || !funnelEl) return;
    const box = funnelEl.getBoundingClientRect();
    svg.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`);
    let path = '';
    // Green "survival" ribbon: connect this stage's onward (green) segment to the
    // NEXT stage's green segment, so it tapers green → green. Only when the next
    // stage has no green at all (e.g. 100%-Fail tests) fall back to its whole bar
    // so the stage doesn't float disconnected.
    const goodSeg = (i) => stageRefs.current[i]?.querySelector('.fseg.good');
    const barEl = (i) => stageRefs.current[i]?.querySelector('.fbar');
    for (let i = 0; i < stages.length - 1; i++) {
      const a = goodSeg(i);
      const b = goodSeg(i + 1) || barEl(i + 1);
      if (!a || !b) continue;
      const ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect();
      const x1 = ra.right - box.left, y1a = ra.top - box.top, y1b = ra.bottom - box.top;
      const x2 = rb.left - box.left, y2a = rb.top - box.top, y2b = rb.bottom - box.top;
      const mx = (x1 + x2) / 2;
      path += `<path d="M${x1},${y1a} C${mx},${y1a} ${mx},${y2a} ${x2},${y2a} L${x2},${y2b} C${mx},${y2b} ${mx},${y1b} ${x1},${y1b} Z" fill="#2E9E5B" opacity="0.12"/>`;
    }
    svg.innerHTML = path;
  }, [stages]);

  useEffect(() => {
    const raf = requestAnimationFrame(drawRibbons);
    window.addEventListener('resize', drawRibbons);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', drawRibbons); };
  }, [drawRibbons]);

  if (!funnel || stages.length === 0) {
    return <div className="funnel-empty">No funnel data yet. Click <strong>Build funnel</strong> to build it.</div>;
  }

  const pct = (n, t) => (t > 0 ? Math.round((n / t) * 100) : 0);
  const footFor = (stage) => {
    const fail = stage.segments.find(s => s.key === 'fail')?.count || 0;
    const good = stage.segments.find(s => s.key === 'good')?.count || 0;
    if (stage.is_test) return mode === 'flow' ? `${fail} blocked by failing tests` : `${fail} failing tests`;
    if (stage.is_coverage) {
      const noTest = stage.segments.find(s => s.key === 'bad')?.count || 0;
      return `${noTest} with no test`;
    }
    if (stage.terminal) return '';
    return mode === 'flow' ? `${good} flow onward →` : `${good} trace onward →`;
  };
  const drillItems = (selected && drill[selected]) || [];
  const selStage = stages.find(s => selected && selected.startsWith(`${s.id}-`));
  const selSeg = selStage?.segments.find(seg => selected === `${selStage.id}-${seg.key}`);

  return (
    <div className={`funnel-view${compact ? ' funnel-compact' : ''}${!compact && !showDrill ? ' funnel-nodrill' : ''}`}>
      {showModes && (
      <div className="funnel-mode-row">
        <span className="funnel-mode-label">Count by</span>
        <div className="funnel-mode-toggle" role="tablist" aria-label="Funnel count mode">
          <button type="button" role="tab" aria-selected={mode === 'level'}
            className={mode === 'level' ? 'active' : ''} onClick={() => setMode('level')}>
            Per level
          </button>
          <button type="button" role="tab" aria-selected={mode === 'flow'}
            className={mode === 'flow' ? 'active' : ''} onClick={() => setMode('flow')}>
            Requirement flow
          </button>
        </div>
        <span className="funnel-mode-hint">
          {mode === 'level'
            ? 'Real item count at each level'
            : `How the ${kpis.total_l1} requirements flow down`}
        </span>

        <span className="funnel-help" tabIndex={0} role="button" aria-label="What do the two modes mean?">
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zM9 8a1 1 0 112 0c0 .38-.18.62-.7 1-.66.48-1.3 1.06-1.3 2.1v.15a1 1 0 002 0c0-.3.15-.5.6-.83.7-.5 1.4-1.15 1.4-2.42a3 3 0 10-6 0 1 1 0 002 0zm2 6.5a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
          </svg>
          <span className="funnel-tooltip" role="tooltip">
            <span className="ftt-row"><strong>Per level</strong> — the real number of <em>items</em> at each level (products, software, tests), as in Orcanos. Green = traces to the next level; the test stage splits Pass / Fail / Not-executed.</span>
            <span className="ftt-row"><strong>Requirement flow</strong> — how many of your <em>user requirements</em> reach each level, narrowing down the chain. The final green = requirements whose tests all pass.</span>
            <span className="ftt-note">The two can differ at a stage: several requirements often share one item (fan-in), so “flow” can be higher than “per level.”</span>
          </span>
        </span>
      </div>
      )}

      {showKpis && (
      <section className="funnel-kpis">
        <div className="fkpi">
          <div className="fkpi-lbl">End-to-end verified</div>
          <div className="fkpi-val">{kpis.verified}<span className="fkpi-unit"> / {kpis.total_l1} {stages[0]?.name || 'L1'}</span></div>
          <div className="fkpi-sub">{kpis.has_test_stage ? 'all linked tests passed' : 'chain reaches the final level'}</div>
        </div>
        <div className="fkpi">
          <div className="fkpi-lbl">Coverage leak</div>
          <div className="fkpi-val">{kpis.leak_pct}<span className="fkpi-unit">%</span></div>
          <div className="fkpi-sub">{stages[0]?.name || 'L1'} not fully verified</div>
        </div>
        {kpis.has_test_stage && (
          <div className="fkpi">
            <div className="fkpi-lbl">Failing tests</div>
            <div className="fkpi-val">{kpis.failing_tests}</div>
            <div className="fkpi-sub">blocking {kpis.blocked_requirements} requirement{kpis.blocked_requirements === 1 ? '' : 's'}</div>
          </div>
        )}
        {kpis.has_test_stage && (
          <div className="fkpi">
            <div className="fkpi-lbl">Tests not executed</div>
            <div className="fkpi-val">{kpis.tests_not_run}</div>
            <div className="fkpi-sub">no run / not in execution</div>
          </div>
        )}
      </section>
      )}

      {showLegend && (
      <div className="funnel-legend">
        {Array.isArray(chrome.legend)
          ? chrome.legend.map((l) => (
            <span key={l.label}><i className={`fsw ${l.kind}`} /> {l.label}</span>
          ))
          : <>
            <span><i className="fsw good" /> Covered / traces onward</span>
            <span><i className="fsw bad" /> Not covered — leaks here</span>
            {kpis.has_test_stage && <>
              <span><i className="fsw good" /> Test: Pass</span>
              <span><i className="fsw bad" /> Test: Fail</span>
              <span><i className="fsw warn" /> Test: Not executed</span>
            </>}
          </>}
        {showDrill && <span className="legend-hint">Click a segment to drill down ↓</span>}
      </div>
      )}

      <div className="funnel" ref={funnelRef}>
        <svg className="funnel-ribbons" ref={ribbonsRef} preserveAspectRatio="none" aria-hidden="true" />
        <div className="funnel-stages">
          {stages.map((stage, i) => {
            const effBarH = Math.max(barMax * ((stage.total || 0) / maxTotal), compact ? 28 : 40);
            // A terminal stage that is one whole segment would otherwise read "100 %" — on a
            // compliance funnel the worst possible caption, since it is the *outcome* stage.
            // `pct_of` lets it be a percentage of the population it should be judged against
            // (Training: 13 signed = 8 % of the 158 tasks owed, not 100 % of the 13).
            const denom = stage.pct_of || stage.total;
            return (
              <div className="fstage" key={stage.id} ref={el => (stageRefs.current[i] = el)}>
                <div className="fstage-head">
                  <div className="fstage-name">{stage.name}</div>
                  <div className="fstage-count">{stage.total} {stage.unit || unit}</div>
                </div>
                <div className="fbar" style={{ height: `${effBarH}px` }}>
                  {stage.segments.map(seg => {
                    if (seg.count === 0) return null;
                    const segKey = `${stage.id}-${seg.key}`;
                    // Pick how much text fits from the segment's real pixel height
                    const segPx = (seg.count / stage.total) * effBarH;
                    const tier = segPx >= 62 ? '' : segPx >= 42 ? 'compact' : segPx >= 24 ? 'tiny' : 'mini';
                    return (
                      <div
                        key={seg.key}
                        className={`fseg ${KIND_CLASS[seg.kind]} ${tier} ${showDrill && selected === segKey ? 'sel' : ''}`}
                        style={{ flex: `${seg.count} ${seg.count} 0` }}
                        onClick={showDrill ? () => setSelected(segKey) : undefined}
                        title={`${seg.label}: ${seg.count} (${pct(seg.count, denom)}%)`}
                      >
                        <span className="fseg-pct">{pct(seg.count, denom)}%</span>
                        <span className="fseg-tag">{seg.label}</span>
                        <span className="fseg-n">{seg.count}</span>
                      </div>
                    );
                  })}
                </div>
                {/* A stage may carry its own foot (the Training funnel's hints, which are what
                    tell the three drops apart); otherwise it is derived from the segments. */}
                {showFoots && (
                  <div className={`fstage-foot ${stage.foot_kind
                    || ((stage.is_test || stage.is_coverage) ? 'leak' : 'survive')}`}>
                    {stage.foot !== undefined ? stage.foot : footFor(stage)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showDrill && (
      <div className="funnel-drill">
        <div className="fdrill-head">
          {selSeg && <span className={`fchip ${KIND_CLASS[selSeg.kind]}`}>{selSeg.label}</span>}
          <h3>{selStage ? `${selStage.name} — ${selSeg?.label}` : 'Details'}</h3>
          <span className="fdrill-hint">
            {selSeg?.count || 0} items{drillItems.length < (selSeg?.count || 0) ? ` · showing ${drillItems.length}` : ''}
          </span>
          {onViewInMatrix && (
            <button className="fto-matrix" onClick={() => onViewInMatrix(selected)}>View in Matrix →</button>
          )}
        </div>
        {drillItems.length === 0 ? (
          <div className="fdrill-empty">Nothing in this segment.</div>
        ) : (
          <ul className="fdrill-items">
            {drillItems.map((it, idx) => (
              <li key={idx}>
                <span className="fk">{it.key}</span>
                <span className="fnm">{it.name}</span>
                <span className={`fst ${STATUS_KIND[it.status] || 'warn'}`}>{STATUS_LABEL[it.status] || it.status}</span>
                {it.url
                  ? <a className="fopen" href={it.url} target="_blank" rel="noopener noreferrer">Open in Orcanos ↗</a>
                  : <span className="fopen fopen-off">—</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      )}
    </div>
  );
}

export default FunnelView;
